import type { ComponentType, LazyExoticComponent } from 'react';
import { lazyWithRetry as lazyWithRetryBase } from '@doxyme/ui/utils';
import { getLogger } from '../libs/logger';

const logger = getLogger('lazyWithRetry');

/**
 * Thin wrapper around the UI library `lazyWithRetry` that wires retry
 * telemetry to our Datadog-backed logger. Prefer this helper over
 * `React.lazy` for dynamic imports within `apps/frontend` so transient
 * chunk-load failures surface in observability.
 *
 * @param factory Dynamic import factory, same shape as `React.lazy`.
 * @param chunkName Optional human-friendly identifier included in log lines.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkName?: string,
): LazyExoticComponent<T> =>
  lazyWithRetryBase(factory, {
    onRetry: ({ attempt, error, delayMs }) =>
      logger.warn('lazy import failed', {
        chunkName,
        attempt,
        error,
        delayMs,
      }),
    onSuccessAfterRetry: ({ attempts }) =>
      logger.info('lazy import succeeded after retry', {
        chunkName,
        attempts,
      }),
    onFinalFailure: ({ attempts, error }) =>
      logger.error('lazy import failed permanently', {
        chunkName,
        attempts,
        error,
      }),
  });
